import { ValidatorFn, Validators } from "@angular/forms";
import { ColumnDef } from "./column-definition";

/**
 * Credit plan interface to receive from the API
 */
export interface CreditPlanInterface {
    id: number;
    name: string;
    active: boolean;
    apr: number;
    duration: number;
    mdr: number;
    notes: string | null;
    description: string | null;
    supplierId: string | null;
};

/**
 * Formal credit plan class definition
 */
export class CreditPlan {
    /** Attribute fields */
    private _id: number;
    private _name: string;
    private _active: boolean;
    private _apr: number;
    private _duration: number;
    private _mdr: number;
    private _notes: string;
    private _description: string;
    private _supplier: string | null;

    /** Validators for the credit plan */
    public static Validators = new Map<string, ValidatorFn[]>([
        ["name", [
            Validators.required,
            Validators.maxLength(255),
        ]],
        ["active", [
            Validators.required,
        ]],
        ["apr", [
            Validators.required,
            Validators.min(0),
            Validators.max(100),
        ]],
        ["duration", [
            Validators.required,
            Validators.min(0),
        ]],
        ["mdr", [
            Validators.required,
            Validators.min(0),
            Validators.max(100),
        ]],
        ["notes", [
            Validators.maxLength(255),
        ]],
        ["description", [
            Validators.maxLength(255),
        ]],
        ["supplier", [
            Validators.required,
        ]],
    ]);

    /**
     * Construct the credit plan from an interface
     */
    constructor(intf: CreditPlanInterface) {
        this._id = intf.id;
        this._name = intf.name || "";
        this._active = intf.active;
        this._apr = intf.apr;
        this._duration = intf.duration;
        this._mdr = intf.mdr;
        this._notes = intf.notes || "";
        this._description = intf.description || "";
        this._supplier = intf.supplierId;
    }

    /**
     * Determine if two credit plans are equal
     * 
     * @param plan The other credit plan to check
     * @returns True if credit plans are equal
     */
    public equals(plan: CreditPlan): boolean {
        return this.id === plan.id &&
            this.name === plan.name &&
            this.active === plan.active &&
            this.apr === plan.apr &&
            this.duration === plan.duration &&
            this.mdr === plan.mdr &&
            this.notes === plan.notes &&
            this.description === plan.description &&
            this.supplier === plan.supplier;
    }

    /**
     * Return a copy of the credit plan
     */
    public copyOf(): CreditPlan {
        return new CreditPlan(this.asInterface());
    }

    /**
     * Return the credit plan as a string representation, used for filtering/searching
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /**
     * Return a map of differences with the column name of another column definition
     * 
     * @param plan The other credit plan to compare
     * @returns A map of differences between the two credit plans
     */
    public differences(plan: CreditPlan): Map<string, string> {
        // Allowed credit plan fields that count as differences
        const differenceAllowedFields: string[] = ["name", "active", "apr", "duration", "mdr", "notes", "description", "supplier"];

        // Loop through each column definition to find differences
        let diffs: Map<string, string> = new Map<string, string>();
        for (let attr of differenceAllowedFields) {
            if ((this as any)[attr] !== (plan as any)[attr]) {
                diffs.set(attr, (plan as any)[attr]);
            }
        }

        return diffs;
    }

    /**
     * Return interface representation of the credit plan
     * 
     * @returns The credit plan as an interface
     */
    public asInterface(): CreditPlanInterface {
        const intf: CreditPlanInterface = {
            id: this.id,
            name: this.name,
            active: this.active,
            apr: this.apr,
            duration: this.duration,
            mdr: this.mdr,
            notes: this.notes,
            description: this.description,
            supplierId: this.supplier
        };

        return intf;
    }


    /** Setters */
    set id(id: number) {
        this._id = id;
    }

    set name(name: string) {
        this._name = name;
    }

    set active(active: boolean) {
        this._active = active;
    }

    set apr(apr: number) {
        this._apr = apr;
    }

    set duration(duration: number) {
        this._duration = duration;
    }

    set mdr(mdr: number) {
        this._mdr = mdr;
    }

    set notes(notes: string) {
        this._notes = notes;
    }

    set description(description: string) {
        this._description = description;
    }

    set supplier(supplier: string | null) {
        this._supplier = supplier;
    }

    /** Getters */
    get id(): number {
        return this._id;
    }

    get name(): string {
        return this._name;
    }

    get active(): boolean {
        return this._active;
    }

    get apr(): number {
        return this._apr;
    }

    get duration(): number {
        return this._duration;
    }

    get mdr(): number {
        return this._mdr;
    }

    get notes(): string {
        return this._notes;
    }

    get description(): string {
        return this._description;
    }

    get supplier(): string | null {
        return this._supplier;
    }
}